import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, getToken } from '../api/client';
import { CountdownOverlay } from '../components/CountdownOverlay';
import { DezoomDots } from '../components/DezoomDots';
import { GuessForm } from '../components/GuessForm';
import { PlayZoomStage } from '../components/PlayZoomStage';
import { ZoomImage } from '../components/ZoomImage';
import { useGameCountdown } from '../hooks/useGameCountdown';
import { useAuth } from '../hooks/useAuth';
import { GuestAuthRequiredModal } from '../components/GuestOnboardingBlock';
import { useLocale, t } from '../hooks/useT';
import { preloadImages } from '../utils/imageCache';
import { currentZoomImageUrl, mergeZoomLevel } from '../utils/zoomLevels';

interface ArchiveResponse {
  puzzle: { id: string; puzzleNumber: number };
  publishDate: string;
  zoomLevels: { level: number; imageUrl: string }[];
  currentZoomLevel: number;
  imageUrl?: string;
  run: {
    id: string;
    status: string;
    dezoomsUsed?: number;
    currentZoomLevel?: number;
    startedAt?: string;
  } | null;
}

interface GuessResponse {
  correct: boolean;
  status: string;
  dezoomsUsed: number;
  currentZoomLevel: number;
  score: number | null;
  shareText: string | null;
  durationSec: number | null;
  answer?: string;
  answerEn?: string;
  imageUrl?: string;
  isReplay?: boolean;
  freshnessMultiplier?: number | null;
}

function formatArchiveDate(dateStr: string, locale: string) {
  const [y, m, d] = dateStr.slice(0, 10).split('-').map(Number);
  return new Intl.DateTimeFormat(locale === 'en' ? 'en-GB' : 'fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(y, m - 1, d));
}

export function ArchivePlayPage() {
  const { puzzleNumber } = useParams<{ puzzleNumber: string }>();
  const [searchParams] = useSearchParams();
  const replayMode = searchParams.get('replay') === '1';
  const navigate = useNavigate();
  const { locale } = useLocale();
  const { user, loginGuest, loading: authLoading } = useAuth();

  const [archive, setArchive] = useState<ArchiveResponse | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [zoomLevel, setZoomLevel] = useState(0);
  const [zoomLevels, setZoomLevels] = useState<{ level: number; imageUrl: string }[]>([]);
  const [dezooms, setDezooms] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [timerSec, setTimerSec] = useState(0);
  const [skipCountdown, setSkipCountdown] = useState(false);
  const [initDone, setInitDone] = useState(false);
  const [isReplay, setIsReplay] = useState(false);
  const [authRequired, setAuthRequired] = useState(false);

  const startRun = useCallback(async () => {
    if (runId || !puzzleNumber) return;
    const res = await api<{ runId: string; startedAt: string; isReplay?: boolean; resumed?: boolean }>(
      `/archives/${puzzleNumber}/runs/start`,
      { method: 'POST' },
    );
    setRunId(res.runId);
    setIsReplay(!!res.isReplay);
    if (!res.resumed) {
      setZoomLevel(0);
      setDezooms(0);
    }
    setStartedAt(new Date(res.startedAt).getTime());
    setTimerSec(0);
  }, [runId, puzzleNumber]);

  const { display, revealed, ready } = useGameCountdown({
    enabled: initDone && !skipCountdown,
    skip: initDone && skipCountdown,
    onGo: startRun,
  });

  const init = useCallback(async () => {
    if (!puzzleNumber) return;
    if (!getToken() && !user) {
      await loginGuest();
    }
    const data = await api<ArchiveResponse>(`/archives/${puzzleNumber}`);
    const levels = data.zoomLevels || [];
    const inProgress = data.run?.status === 'in_progress';
    const startLevel = inProgress
      ? (data.run?.currentZoomLevel ?? data.currentZoomLevel)
      : 0;

    setArchive(data);
    setZoomLevels(levels);
    setZoomLevel(startLevel);
    const preloadUrl = currentZoomImageUrl(levels, startLevel, data.imageUrl);
    if (preloadUrl) preloadImages([preloadUrl]);

    if (inProgress && data.run) {
      setDezooms(data.run.dezoomsUsed ?? 0);
      setZoomLevel(data.run.currentZoomLevel ?? 0);
      setRunId(data.run.id);
      if (data.run.startedAt) {
        setStartedAt(new Date(data.run.startedAt).getTime());
      }
      setSkipCountdown(true);
    } else if (data.run && !replayMode) {
      navigate('/result', {
        state: { fromRun: data.run.id, isArchive: true },
        replace: true,
      });
      return;
    } else {
      setRunId(null);
      setDezooms(0);
      setZoomLevel(0);
      setStartedAt(null);
      setTimerSec(0);
      setIsReplay(false);
      setSkipCountdown(false);
    }

    setInitDone(true);
  }, [puzzleNumber, user, loginGuest, navigate, replayMode]);

  useEffect(() => {
    if (authLoading) return;
    setInitDone(false);
    init().catch((e) => {
      const code = (e as { data?: { error?: string } }).data?.error;
      if (code === 'use_today_play') {
        navigate('/play', { replace: true });
        return;
      }
      if (code === 'auth_required') {
        setAuthRequired(true);
        setInitDone(true);
        return;
      }
      setError((e as Error).message);
    });
  }, [authLoading, init, navigate]);

  useEffect(() => {
    if (!startedAt) return;
    const id = setInterval(() => {
      setTimerSec(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  const handleGuess = async (guess: string): Promise<boolean | void> => {
    if (!runId) return;
    setBusy(true);
    setError('');
    try {
      const res = await api<GuessResponse>(`/runs/${runId}/guess`, {
        method: 'POST',
        body: JSON.stringify({ guess }),
      });

      setDezooms(res.dezoomsUsed);
      setZoomLevel(res.currentZoomLevel);
      if (res.imageUrl) {
        setZoomLevels((prev) => mergeZoomLevel(prev, res.currentZoomLevel, res.imageUrl));
        preloadImages([res.imageUrl]);
      }

      if (res.status !== 'in_progress') {
        navigate('/result', {
          state: {
            won: res.status === 'won',
            score: res.score ?? 0,
            dezoomsUsed: res.dezoomsUsed,
            durationSec: res.durationSec ?? 0,
            shareText: res.shareText,
            puzzleNumber: archive?.puzzle.puzzleNumber ?? 0,
            puzzleId: archive?.puzzle.id,
            isReplay: res.isReplay,
            freshnessMultiplier: res.freshnessMultiplier,
            isArchive: true,
            zoom0ImageUrl: zoomLevels.find((z) => z.level === 0)?.imageUrl ?? zoomLevels[0]?.imageUrl,
            answer: res.answer,
            answerEn: res.answerEn,
            imageUrl: res.imageUrl,
          },
        });
        return;
      }
      return res.correct;
    } catch (e: unknown) {
      const err = e as { data?: { error?: string } };
      setError(err.data?.error || t('common.error'));
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const formatTimer = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (authRequired) {
    if (user?.isGuest) {
      return (
        <main className="page play">
          <GuestAuthRequiredModal onClose={() => navigate('/archives')} />
        </main>
      );
    }
    return (
      <main className="page play">
        <div className="result-card">
          <h1>{t('archives.guestLimit')}</h1>
          <p className="muted">{t('archives.guestLimitPlay')}</p>
          <Link to="/login" className="btn primary">{t('landing.login')}</Link>
          <Link to="/archives" className="btn secondary">{t('archives.back')}</Link>
        </div>
      </main>
    );
  }

  if (!archive) {
    return (
      <main className="page play">
        <p className="loading">{error || t('common.loading')}</p>
      </main>
    );
  }

  return (
    <main className="page play">
      <header className="play-header">
        <div className="play-header-left">
          <Link to="/archives" className="link-muted play-back">{t('archives.back')}</Link>
          <span className="puzzle-num practice-badge">
            {isReplay ? t('archives.replayBadge') : t('archives.badge')} #{archive.puzzle.puzzleNumber}
          </span>
          <span className="muted play-archive-date">
            {formatArchiveDate(archive.publishDate, locale)}
          </span>
          {startedAt != null && (
            <span className="play-timer" aria-live="polite">{formatTimer(timerSec)}</span>
          )}
        </div>
        <DezoomDots used={dezooms} />
      </header>

      <PlayZoomStage revealed={revealed} countdown={<CountdownOverlay value={display} />}>
        <ZoomImage levels={zoomLevels} activeLevel={zoomLevel} />
      </PlayZoomStage>

      <footer className="play-footer">
        {error && <p className="play-error" role="alert">{error}</p>}
        <GuessForm onSubmit={handleGuess} disabled={busy || !ready || !runId} />
      </footer>
    </main>
  );
}
